import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { apiGetJson, apiPutJson } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import { useUserContext } from "../contexts/UserContext";
import { useMallContext } from "../contexts/MallContext";
import { isFreePlanUser, isLightPlanUser } from "../utils/plan";
import { openUpgradeForm } from "../components/FreePlanUpgradeDialog";
import "./ItemProfitSummaryPage.css";

type ApiSummary = {
    totalSales?: number | null;
    totalMarginProfit?: number | null;
    totalOperatingProfit?: number | null;
    avgMarginRate?: number | null;
    avgOperatingRate?: number | null;
    marginDeficitCount?: number | null;
    operatingDeficitCount?: number | null;
    productCount?: number | null;
};

type ApiRow = {
    itemKey?: string;
    itemManagementNumber?: string;
    itemNumber?: string;
    skuManagementNumber?: string | null;
    skuNumber?: string | null;
    itemCode?: string;
    skuCode?: string | null;
    itemName?: string;
    genreName?: string;
    imageUrl?: string;

    units?: number | null;
    orders?: number | null;
    sales?: number | null;

    cost?: number | null;
    costRate?: number | null;

    feeUnit?: number | null;
    feeRate?: number | null;

    profitUnit?: number | null;
    profitRate?: number | null;

    fixedCostUnit?: number | null;
    operatingProfitUnit?: number | null;
    operatingProfitRate?: number | null;

    adCostTotal?: number | null;
    adCostRate?: number | null;
    rppRoas12h?: number | null;
    rppRoas720h?: number | null;
    caRoas?: number | null;

    costSource?: string | null;
    costMissingFlag?: number | null;

    // 送料別の商品：お客様が払った送料（sales に含む）と、送料別の明細があったか（楽天のみ）
    shippingRevenueAmount?: number | null;
    hasSeparateShipping?: number | null;

    momProfitDiff?: number | null;
    momProfitChangeRate?: number | null;
    momOpProfitDiff?: number | null;
    momOpProfitChangeRate?: number | null;
};

type ApiRes = {
    shopUrl: string;
    mode?: "month" | "custom";
    range: {
        startDate: string;
        endDate: string;
    };
    availableRange?: {
        minStartDate?: string | null;
        maxEndDate?: string | null;
    };
    summary?: ApiSummary;
    rows: ApiRow[];
};

type ModeKey = "month" | "custom";
type SortKey =
    | "sales"
    | "qty"
    | "profit"
    | "deficit"
    | "profitRate"
    | "adCost"
    | "roas12h"
    | "roas720h"
    | "caRoas"
    | "cost"
    | "costRate"
    | "feeUnit"
    | "feeRate";
type SortDir = "asc" | "desc";
type TabKey = "all" | "top" | "worst";
type TemplateKey = "A" | "B" | "C";
type ProfitMode = "margin" | "operating";
type AggregateUnit = "sku" | "item";

const STORAGE_KEYS = {
    mode: "item-profit-summary:mode",
    profitMode: "item-profit-summary:profitMode",
    aggregateUnit: "item-profit-summary:aggregateUnit",
} as const;

function readStoredMode(): ModeKey {
    try {
        const v = localStorage.getItem(STORAGE_KEYS.mode);
        return v === "custom" ? "custom" : "month";
    } catch {
        return "month";
    }
}

function readStoredProfitMode(): ProfitMode {
    try {
        const v = localStorage.getItem(STORAGE_KEYS.profitMode);
        return v === "operating" ? "operating" : "margin";
    } catch {
        return "margin";
    }
}

function readStoredAggregateUnit(): AggregateUnit {
    try {
        const v = localStorage.getItem(STORAGE_KEYS.aggregateUnit);
        return v === "item" ? "item" : "sku";
    } catch {
        return "sku";
    }
}

type DisplayRow = ApiRow & {
    displayProfit: number | null;
    displayProfitRate: number | null;
    displayDeficit: number;
    sortProfit: number;
    sortProfitRate: number;
    status: "good" | "warn" | "bad" | "unknown";
};

// 右上の並び替えボタンで選べるキー（どのテンプレートでも有効）
const BUTTON_SORT_KEYS: SortKey[] = ["sales", "qty", "profit", "deficit"];

// 列見出しで選べるキー（テンプレートごとに表示される列）
const TEMPLATE_SORT_KEYS: Record<TemplateKey, SortKey[]> = {
    A: ["sales", "profit", "profitRate", "qty"],
    B: ["sales", "adCost", "roas12h", "roas720h", "caRoas", "profit"],
    C: ["sales", "cost", "costRate", "feeUnit", "feeRate", "profit", "profitRate"],
};

// 並び替えに使う値。null は「値なし」で、昇順・降順とも末尾に置く
function getSortValue(r: DisplayRow, key: SortKey): number | null {
    switch (key) {
        case "qty":
            return num(r.units);
        case "profit":
            return r.displayProfit;
        case "deficit":
            return num(r.displayDeficit);
        case "profitRate":
            return r.displayProfitRate;
        case "adCost":
            return nullableNum(r.adCostTotal);
        case "roas12h":
            return nullableNum(r.rppRoas12h);
        case "roas720h":
            return nullableNum(r.rppRoas720h);
        case "caRoas":
            return nullableNum(r.caRoas);
        case "cost":
            return nullableNum(r.cost);
        case "costRate":
            return nullableNum(r.costRate);
        case "feeUnit":
            return nullableNum(r.feeUnit);
        case "feeRate":
            return nullableNum(r.feeRate);
        case "sales":
        default:
            return num(r.sales);
    }
}

const nf0 = new Intl.NumberFormat("ja-JP", { maximumFractionDigits: 0 });
const MIN_SELECTABLE_DATE_FLOOR = "2026-04-01";

function clampDate(date: string, minDate: string, maxDate: string) {
    if (!date) return minDate;
    if (date < minDate) return minDate;
    if (date > maxDate) return maxDate;
    return date;
}

function getMonthRange(monthOffset: number) {
    const targetMonth = dayjs().add(monthOffset, "month");
    return {
        startDate: targetMonth.startOf("month").format("YYYY-MM-DD"),
        endDate: targetMonth.endOf("month").format("YYYY-MM-DD"),
    };
}

function num(v: unknown): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function nullableNum(v: unknown): number | null {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function fmtYen(v: unknown) {
    const n = nullableNum(v);
    if (n === null) return "—";
    if (n < 0) return `−¥${nf0.format(Math.abs(Math.round(n)))}`;
    return `¥${nf0.format(Math.round(n))}`;
}

function fmtPct(v: unknown, digits = 1) {
    const n = nullableNum(v);
    if (n === null) return "—";
    return `${(n * 100).toFixed(digits)}%`;
}

function fmtRoasWhole(v: unknown) {
    const n = nullableNum(v);
    if (n === null) return "データなし";
    return `${Math.round(n)}%`;
}

function toCsv(rows: DisplayRow[], profitMode: ProfitMode) {
    const headers = [
        "商品管理番号",
        "商品番号",
        "SKU管理番号",
        "SKU番号",
        "商品名",
        "売上",
        profitMode === "margin" ? "限界利益額" : "営業利益額",
        profitMode === "margin" ? "限界利益率" : "営業利益率",
        "個数",
        "原価ソース",
        "前月同期比",
    ];

    const esc = (x: unknown) => {
        if (x === null || x === undefined) return "";
        const s = String(x);
        if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    };

    const fmtCsvProfit = (value: number | null) => {
        if (value === null || value === undefined) return "未算出";
        return Math.round(value);
    };

    const fmtCsvRate = (value: number | null) => {
        if (value === null || value === undefined) return "未算出";
        return `${(value * 100).toFixed(1)}%`;
    };

    const body = rows.map((r) => [
        r.itemManagementNumber ?? r.itemCode ?? "",
        r.itemNumber ?? "",
        r.skuManagementNumber ?? r.skuCode ?? "",
        r.skuNumber ?? "",
        r.itemName ?? "",
        Math.round(num(r.sales)),
        fmtCsvProfit(r.displayProfit),
        fmtCsvRate(r.displayProfitRate),
        Math.round(num(r.units)),
        r.costSource ?? "",
        profitMode === "margin"
            ? (r.momProfitChangeRate !== null && r.momProfitChangeRate !== undefined
                ? `${r.momProfitChangeRate >= 0 ? "+" : ""}${(r.momProfitChangeRate * 100).toFixed(1)}%`
                : "")
            : (r.momOpProfitChangeRate !== null && r.momOpProfitChangeRate !== undefined
                ? `${r.momOpProfitChangeRate >= 0 ? "+" : ""}${(r.momOpProfitChangeRate * 100).toFixed(1)}%`
                : ""),
    ]);

    return "\ufeff" + [headers, ...body].map((row) => row.map(esc).join(",")).join("\n");
}

function downloadCsv(filename: string, content: string) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function getRowStatus(profitRate: number | null): "good" | "warn" | "bad" | "unknown" {
    if (profitRate === null) return "unknown";
    if (profitRate < 0) return "bad";
    if (profitRate >= 0.3) return "good";
    return "warn";
}

function aggregateRowsByItem(rows: DisplayRow[], profitMode: ProfitMode): DisplayRow[] {
    const groups = new Map<string, DisplayRow[]>();

    for (const row of rows) {
        const key = String(
            row.itemManagementNumber || row.itemCode || row.itemName || row.itemKey || "",
        ).trim();
        if (!key) continue;
        const list = groups.get(key);
        if (list) {
            list.push(row);
        } else {
            groups.set(key, [row]);
        }
    }

    const calcWeightedAverage = (
        list: DisplayRow[],
        getter: (row: DisplayRow) => number | null,
        weightGetter: (row: DisplayRow) => number,
    ): number | null => {
        let totalWeight = 0;
        let totalValue = 0;
        for (const row of list) {
            const value = getter(row);
            if (value === null) continue;
            const weight = Math.max(0, weightGetter(row));
            if (weight <= 0) continue;
            totalWeight += weight;
            totalValue += value * weight;
        }
        if (totalWeight <= 0) return null;
        return totalValue / totalWeight;
    };

    return Array.from(groups.values()).map((list) => {
        const base = list[0];
        const totalSales = list.reduce((acc, row) => acc + num(row.sales), 0);
        const totalUnits = list.reduce((acc, row) => acc + num(row.units), 0);
        const totalOrders = list.reduce((acc, row) => acc + num(row.orders), 0);
        const totalCost = list.reduce((acc, row) => acc + num(row.cost), 0);
        const totalFee = list.reduce((acc, row) => acc + num(row.feeUnit), 0);
        const totalAdCost = list.reduce((acc, row) => acc + num(row.adCostTotal), 0);
        const marginProfits = list
            .map((row) => nullableNum(row.profitUnit))
            .filter((v): v is number => v !== null);
        const totalMarginProfit = marginProfits.length > 0
            ? marginProfits.reduce((acc, v) => acc + v, 0)
            : null;

        const operatingProfits = list
            .map((row) => {
                const op = nullableNum(row.operatingProfitUnit);
                if (op !== null) return op;
                const margin = nullableNum(row.profitUnit);
                const fixed = nullableNum(row.fixedCostUnit);
                if (margin !== null && fixed !== null) return margin - fixed;
                return null;
            })
            .filter((v): v is number => v !== null);
        const totalOperatingProfit = operatingProfits.length > 0
            ? operatingProfits.reduce((acc, v) => acc + v, 0)
            : null;

        const totalFixedCost = list.reduce((acc, row) => acc + num(row.fixedCostUnit), 0);

        const marginRate = totalSales > 0 && totalMarginProfit !== null
            ? totalMarginProfit / totalSales
            : null;
        const operatingRate = totalSales > 0 && totalOperatingProfit !== null
            ? totalOperatingProfit / totalSales
            : null;

        const merged: DisplayRow = {
            ...base,
            skuManagementNumber: null,
            skuNumber: null,
            skuCode: null,
            units: totalUnits,
            orders: totalOrders,
            sales: totalSales,
            cost: totalCost,
            costRate: totalSales > 0 ? totalCost / totalSales : null,
            feeUnit: totalFee,
            feeRate: totalSales > 0 ? totalFee / totalSales : null,
            profitUnit: totalMarginProfit,
            profitRate: marginRate,
            fixedCostUnit: totalFixedCost,
            operatingProfitUnit: totalOperatingProfit,
            operatingProfitRate: operatingRate,
            adCostTotal: totalAdCost,
            adCostRate: totalSales > 0 ? totalAdCost / totalSales : null,
            rppRoas12h: calcWeightedAverage(list, (row) => nullableNum(row.rppRoas12h), (row) => num(row.sales)),
            rppRoas720h: calcWeightedAverage(list, (row) => nullableNum(row.rppRoas720h), (row) => num(row.sales)),
            caRoas: calcWeightedAverage(list, (row) => nullableNum(row.caRoas), (row) => num(row.sales)),
            costSource: (() => {
                const sources = Array.from(new Set(list.map((row) => String(row.costSource || "").trim()).filter(Boolean)));
                if (sources.length === 0) return null;
                if (sources.length === 1) return sources[0];
                return "mixed";
            })(),
            costMissingFlag: list.some((row) => num(row.costMissingFlag) > 0) ? 1 : 0,
            shippingRevenueAmount: list.reduce((acc, row) => acc + num(row.shippingRevenueAmount), 0),
            hasSeparateShipping: list.some((row) => num(row.hasSeparateShipping) > 0) ? 1 : 0,
            momProfitDiff: list.reduce((acc, row) => acc + num(row.momProfitDiff), 0),
            momOpProfitDiff: list.reduce((acc, row) => acc + num(row.momOpProfitDiff), 0),
            momProfitChangeRate: calcWeightedAverage(list, (row) => nullableNum(row.momProfitChangeRate), (row) => Math.abs(num(row.profitUnit))),
            momOpProfitChangeRate: calcWeightedAverage(list, (row) => nullableNum(row.momOpProfitChangeRate), (row) => Math.abs(num(row.operatingProfitUnit))),
            displayProfit: profitMode === "margin" ? totalMarginProfit : totalOperatingProfit,
            displayProfitRate: profitMode === "margin" ? marginRate : operatingRate,
            displayDeficit: 0,
            sortProfit: 0,
            sortProfitRate: 0,
            status: "unknown",
        };

        const displayProfit = merged.displayProfit;
        const displayProfitRate = merged.displayProfitRate;
        merged.displayDeficit = displayProfit !== null && displayProfit < 0 ? Math.abs(displayProfit) : 0;
        merged.sortProfit = displayProfit ?? Number.NEGATIVE_INFINITY;
        merged.sortProfitRate = displayProfitRate ?? Number.NEGATIVE_INFINITY;
        merged.status = getRowStatus(displayProfitRate);

        return merged;
    });
}

const GENRE_COLORS: Record<string, string> = {
    "キッチン用品": "#378ADD",
    "食品": "#1D9E75",
    "靴": "#EF9F27",
    "レディースファッション": "#E24B4A",
    "インナー・下着": "#9DC3E6",
};

function IppThumb({ imageUrl, genreName }: { imageUrl?: string | null; genreName?: string | null }) {
    const [failed, setFailed] = React.useState(false);
    const genre = String(genreName || "").trim();
    const bg = GENRE_COLORS[genre] || "#9DAAB8";
    const initial = genre ? genre[0] : "?";

    React.useEffect(() => {
        setFailed(false);
    }, [imageUrl]);

    if (imageUrl && !failed) {
        return (
            <img
                className="ipp-thumb"
                src={imageUrl}
                alt=""
                onError={e => {
                    setFailed(true);
                    // 失敗時のイベント・srcも出力
                    console.error("[IppThumb] onError src=", imageUrl, e);
                }}
            />
        );
    }
    return (
        <div className="ipp-thumb-fallback" style={{ background: bg }}>
            {initial}
        </div>
    );
}

const ItemProfitUpgradeDialog = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4">
            <div className="w-full max-w-[480px] overflow-hidden rounded-xl bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                <div className="bg-[#4a72c4] px-7 pb-5 pt-6">
                    <div className="mb-2 text-[11px] font-medium tracking-[0.08em] text-[#cfdcf5]">
                        スタンダードプラン以上の機能
                    </div>
                    <div className="text-[17px] font-semibold leading-[1.5] text-white">
                        商品別利益はスタンダードプラン以上でご利用いただけます
                    </div>
                </div>

                <div className="px-7 py-6">
                    <div className="mb-[18px] grid grid-cols-1 gap-[10px] sm:grid-cols-2">
                        <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
                            <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                                商品ごとの利益を比較
                            </div>
                            <div className="text-[12px] leading-[1.5] text-[#555]">
                                商品管理番号ごとに、本当の利益を自動で集計
                            </div>
                        </div>

                        <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
                            <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                                商品ごとの各種費用を特定
                            </div>
                            <div className="text-[12px] leading-[1.5] text-[#555]">
                                商品ごとにROAS、ポイント、クーポンコストも自動で集計
                            </div>
                        </div>
                    </div>

                    <p className="mb-5 text-[13px] leading-[1.7] text-[#555]">
                        「売上と利益はどう関係している?」売上ではなく
                        <strong className="font-semibold text-[#333]">限界利益額</strong>
                        で判断し、儲かるショップ運営を。
                    </p>

                    <div className="flex gap-[10px] border-t border-[#eee] pt-4">
                        <button
                            className="flex-1 rounded-lg bg-[#4a72c4] px-4 py-[11px] text-[14px] font-semibold text-white transition hover:bg-[#3a5fad]"
                            onClick={() => openUpgradeForm("商品別利益分析")}
                        >
                            プランをアップグレードする →
                        </button>

                        <button
                            className="whitespace-nowrap rounded-lg border border-[#ddd] bg-white px-4 py-[11px] text-[13px] text-[#888] transition hover:bg-[#f5f5f5]"
                            onClick={onClose}
                        >
                            閉じる
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function ItemProfitSummaryPage() {
    const user = useUserContext();
    const { mall } = useMallContext();
    // 送料別の印・絞り込みは楽天のみ（受注の「送料込別」を持つのは楽天だけ）
    const showShippingType = mall === "rakuten";
    const navigate = useNavigate();
    const location = useLocation();

    const today = useMemo(() => {
        const d = new Date();
        return d.toISOString().slice(0, 10);
    }, []);

    const monthStart = useMemo(() => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().slice(0, 10);
    }, []);

    const [mode, setMode] = useState<ModeKey>(() => readStoredMode());
    const [profitMode, setProfitMode] = useState<ProfitMode>(() => readStoredProfitMode());
    const [aggregateUnit, setAggregateUnit] = useState<AggregateUnit>(() => readStoredAggregateUnit());
    const [template, setTemplate] = useState<TemplateKey>("A");
    const [tab, setTab] = useState<TabKey>("all");
    const [sortKey, setSortKey] = useState<SortKey>("sales");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    const [search, setSearch] = useState("");
    const [genreFilter, setGenreFilter] = useState("");
    const [shippingFilter, setShippingFilter] = useState<"" | "separate">("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

    const [startDate, setStartDate] = useState(monthStart);
    const [endDate, setEndDate] = useState(today);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<ApiRes | null>(null);
    const [imageFetchingItemKeys, setImageFetchingItemKeys] = useState<Set<string>>(new Set());
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
    const fetchSeqRef = useRef(0);

    const requestMinDate = MIN_SELECTABLE_DATE_FLOOR;
    const requestMaxDate = today;

    const periodPresets = useMemo(() => ({
        current: {
            startDate: clampDate(monthStart, requestMinDate, requestMaxDate),
            endDate: requestMaxDate,
        },
        previous: getMonthRange(-1),
        twoMonthsAgo: getMonthRange(-2),
    }), [monthStart, requestMinDate, requestMaxDate]);

    const selectedPeriodPreset = useMemo(() => {
        if (startDate === periodPresets.current.startDate && endDate === periodPresets.current.endDate) {
            return "current";
        }
        if (startDate === periodPresets.previous.startDate && endDate === periodPresets.previous.endDate) {
            return "previous";
        }
        if (startDate === periodPresets.twoMonthsAgo.startDate && endDate === periodPresets.twoMonthsAgo.endDate) {
            return "twoMonthsAgo";
        }
        return "custom";
    }, [startDate, endDate, periodPresets]);

    const selectPeriodPreset = useCallback((preset: keyof typeof periodPresets) => {
        const range = periodPresets[preset];
        setMode(preset === "current" ? "month" : "custom");
        setStartDate(clampDate(range.startDate, requestMinDate, requestMaxDate));
        setEndDate(clampDate(range.endDate, requestMinDate, requestMaxDate));
    }, [periodPresets, requestMinDate, requestMaxDate]);

    const minSelectableDate = useMemo(() => {
        const candidate = String(data?.availableRange?.minStartDate || "").trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
            return candidate < MIN_SELECTABLE_DATE_FLOOR
                ? MIN_SELECTABLE_DATE_FLOOR
                : candidate;
        }
        return MIN_SELECTABLE_DATE_FLOOR;
    }, [data?.availableRange?.minStartDate]);

    const maxSelectableDate = useMemo(() => {
        const candidate = String(data?.availableRange?.maxEndDate || "").trim();
        const resolved = /^\d{4}-\d{2}-\d{2}$/.test(candidate) ? candidate : today;
        return resolved < minSelectableDate ? minSelectableDate : resolved;
    }, [data?.availableRange?.maxEndDate, today, minSelectableDate]);

    const isRestrictedPlan = useMemo(
        () => isFreePlanUser(user) || isLightPlanUser(user),
        [user],
    );

    const fetchData = useCallback(async () => {
        if (isRestrictedPlan) {
            setLoading(false);
            setData(null);
            return;
        }

        const seq = ++fetchSeqRef.current;
        setLoading(true);
        setError(null);

        try {
            const clampedStartDate = clampDate(startDate, requestMinDate, requestMaxDate);
            const clampedEndDate = clampDate(endDate, requestMinDate, requestMaxDate);
            const qs = new URLSearchParams();
            qs.set("mode", mode);
            qs.set("profitMode", profitMode);
            qs.set("startDate", clampedStartDate);
            qs.set("endDate", clampedEndDate);

            const res = (await apiGetJson(
                `/api/profit/item-profit-list?${qs.toString()}`,
            )) as ApiRes;

            if (seq !== fetchSeqRef.current) return;
            setData(res);
        } catch (e: any) {
            if (seq !== fetchSeqRef.current) return;
            setError(e?.message || "商品別利益データを取得できませんでした。");
        } finally {
            if (seq !== fetchSeqRef.current) return;
            setLoading(false);
        }
    }, [mode, profitMode, startDate, endDate, isRestrictedPlan, requestMinDate, requestMaxDate]);

    useEffect(() => {
        if (isRestrictedPlan) {
            setShowUpgradeDialog(true);
            return;
        }
        fetchData();
    }, [fetchData, isRestrictedPlan]);

    useEffect(() => {
        const apiStart = String(data?.range?.startDate || "").trim();
        const apiEnd = String(data?.range?.endDate || "").trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(apiStart) || !/^\d{4}-\d{2}-\d{2}$/.test(apiEnd)) {
            return;
        }
        const safeApiStart = clampDate(apiStart, requestMinDate, requestMaxDate);
        const safeApiEnd = clampDate(apiEnd, requestMinDate, requestMaxDate);

        setStartDate((current) => current === safeApiStart ? current : safeApiStart);
        setEndDate((current) => current === safeApiEnd ? current : safeApiEnd);
    }, [
        data?.range,
        requestMinDate,
        requestMaxDate,
    ]);

    useEffect(() => {
        const nextStart = clampDate(startDate, requestMinDate, requestMaxDate);
        if (nextStart !== startDate) {
            setStartDate(nextStart);
            return;
        }

        const nextEnd = clampDate(endDate, requestMinDate, requestMaxDate);
        if (nextEnd !== endDate) {
            setEndDate(nextEnd);
            return;
        }

        if (startDate > endDate) {
            setEndDate(startDate);
        }
    }, [startDate, endDate, requestMinDate, requestMaxDate]);

    useEffect(() => {
        setPage(1);
    }, [mode, profitMode, aggregateUnit, template, tab, sortKey, sortDir, search, genreFilter, shippingFilter, pageSize, startDate, endDate]);

    // テンプレート切替で見出しの列が無くなった場合は、既定（売上の降順）に戻す
    useEffect(() => {
        if (BUTTON_SORT_KEYS.includes(sortKey) || TEMPLATE_SORT_KEYS[template].includes(sortKey)) return;
        setSortKey("sales");
        setSortDir("desc");
    }, [template, sortKey]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEYS.mode, mode);
            localStorage.setItem(STORAGE_KEYS.profitMode, profitMode);
            localStorage.setItem(STORAGE_KEYS.aggregateUnit, aggregateUnit);
        } catch {
            // localStorage が使えない環境では永続化をスキップ
        }
    }, [mode, profitMode, aggregateUnit]);

    const displayRows = useMemo<DisplayRow[]>(() => {
        return (data?.rows ?? []).map((row) => {
            const sales = nullableNum(row.sales);
            const cost = nullableNum(row.cost);
            const feeUnit = nullableNum(row.feeUnit);

            let marginProfit = nullableNum(row.profitUnit);
            if (
                marginProfit === null &&
                sales !== null &&
                cost !== null &&
                feeUnit !== null
            ) {
                marginProfit = sales - cost - feeUnit;
            }

            let marginRate = nullableNum(row.profitRate);
            if (marginRate === null && marginProfit !== null && sales !== null && sales > 0) {
                marginRate = marginProfit / sales;
            }

            const operatingProfitFromApi = nullableNum(row.operatingProfitUnit);
            const fixedCost = nullableNum(row.fixedCostUnit);
            const operatingRateFromApi = nullableNum(row.operatingProfitRate);

            let operatingProfit: number | null = operatingProfitFromApi;
            if (operatingProfit === null && marginProfit !== null && fixedCost !== null) {
                operatingProfit = marginProfit - fixedCost;
            }

            let operatingRate: number | null = operatingRateFromApi;
            if (operatingRate === null && operatingProfit !== null && sales !== null && sales > 0) {
                operatingRate = operatingProfit / sales;
            }

            const displayProfit = profitMode === "margin" ? marginProfit : operatingProfit;
            const displayProfitRate = profitMode === "margin" ? marginRate : operatingRate;

            return {
                ...row,
                displayProfit,
                displayProfitRate,
                displayDeficit: displayProfit !== null && displayProfit < 0 ? Math.abs(displayProfit) : 0,
                sortProfit: displayProfit ?? Number.NEGATIVE_INFINITY,
                sortProfitRate: displayProfitRate ?? Number.NEGATIVE_INFINITY,
                status: getRowStatus(displayProfitRate),
            };
        });
    }, [data?.rows, profitMode]);

    const aggregatedRows = useMemo<DisplayRow[]>(() => {
        if (aggregateUnit === "sku") return displayRows;
        return aggregateRowsByItem(displayRows, profitMode);
    }, [displayRows, aggregateUnit, profitMode]);

    const topWorstKeySet = useMemo(() => {
        const valid = aggregatedRows.filter((r) => r.displayProfit !== null);
        const sorted = [...valid].sort((a, b) => (b.displayProfit ?? 0) - (a.displayProfit ?? 0));
        const top = new Set(sorted.slice(0, 10).map((r) => String(r.itemKey || "")));
        const worst = new Set(sorted.slice(-10).map((r) => String(r.itemKey || "")));
        return { top, worst };
    }, [aggregatedRows]);

    const genreOptions = useMemo(() => {
        const seen = new Set<string>();
        for (const r of aggregatedRows) {
            const g = String(r.genreName || "").trim();
            if (g) seen.add(g);
        }
        return Array.from(seen).sort();
    }, [aggregatedRows]);

    const alerts = useMemo(() => {
        const list: {
            key: string;
            type: "warn" | "red";
            message: string;
            ctaLabel?: string;
            ctaPageId?: string;
        }[] = [];
        const missingCount = aggregatedRows.filter((r) => num(r.costMissingFlag) > 0).length;
        if (missingCount > 0) {
            list.push({
                key: "cost_missing",
                type: "warn",
                message: `原価未設定の商品が ${nf0.format(missingCount)} 件あります。原価を登録すると正確な利益が算出されます。`,
                ctaLabel: "原価を設定する →",
                ctaPageId: "itemCost",
            });
        }
        const deficitCount = aggregatedRows.filter(
            (r) => num(r.costMissingFlag) === 0 && (r.displayProfit ?? 0) < 0,
        ).length;
        if (deficitCount > 0) {
            list.push({
                key: "deficit",
                type: "red",
                message: `赤字商品が ${nf0.format(deficitCount)} 件あります。費用構成を確認してください。`,
            });
        }
        return list;
    }, [aggregatedRows]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const gf = genreFilter.trim();

        let rows = aggregatedRows.filter((r) => {
            if (gf && (r.genreName || "") !== gf) return false;
            if (showShippingType && shippingFilter === "separate" && num(r.hasSeparateShipping) <= 0) return false;
            if (!q) return true;
            const target = [
                r.itemName ?? "",
                r.itemManagementNumber ?? "",
                r.itemNumber ?? "",
                r.skuManagementNumber ?? "",
                r.skuNumber ?? "",
                r.itemCode ?? "",
                r.skuCode ?? "",
                r.genreName ?? "",
            ]
                .join(" ")
                .toLowerCase();

            return target.includes(q);
        });

        if (tab === "top") {
            rows = rows.filter((r) => topWorstKeySet.top.has(String(r.itemKey || "")));
        }
        if (tab === "worst") {
            rows = rows.filter((r) => topWorstKeySet.worst.has(String(r.itemKey || "")));
        }

        rows = [...rows].sort((a, b) => {
            const va = getSortValue(a, sortKey);
            const vb = getSortValue(b, sortKey);
            if (va === null && vb === null) return 0;
            if (va === null) return 1;
            if (vb === null) return -1;
            return sortDir === "desc" ? vb - va : va - vb;
        });

        return rows;
    }, [aggregatedRows, search, genreFilter, shippingFilter, showShippingType, tab, topWorstKeySet.top, topWorstKeySet.worst, sortKey, sortDir]);

    // 右上のボタン：従来どおりそのキーの降順にする
    const selectButtonSort = useCallback((key: SortKey) => {
        setSortKey(key);
        setSortDir("desc");
    }, []);

    // 列見出し：別の列なら降順から、同じ列なら昇順・降順を切り替える
    const toggleHeaderSort = useCallback((key: SortKey) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "desc" ? "asc" : "desc"));
            return;
        }
        setSortKey(key);
        setSortDir("desc");
    }, [sortKey]);

    const renderSortTh = (label: React.ReactNode, key: SortKey) => {
        const active = sortKey === key;
        return (
            <th
                className="right"
                aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
            >
                <button
                    type="button"
                    className={`ipp-th-sort ${active ? "active" : ""}`}
                    onClick={() => toggleHeaderSort(key)}
                    title="クリックで並べ替え"
                >
                    {label}
                    <span className="ipp-th-sort-mark" aria-hidden="true">
                        <span className={active && sortDir === "asc" ? "on" : ""}>▲</span>
                        <span className={active && sortDir === "desc" ? "on" : ""}>▼</span>
                    </span>
                </button>
            </th>
        );
    };

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(filteredRows.length / pageSize));
    }, [filteredRows.length, pageSize]);

    const currentPage = Math.min(page, totalPages);

    const pageRows = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, currentPage, pageSize]);

    const kpiSummary = useMemo(() => {
        const rows = aggregatedRows;

        const totalSales = rows.reduce((acc, row) => acc + num(row.sales), 0);

        const calculableRows = rows.filter((row) => {
            if (num(row.costMissingFlag) > 0) return false;
            if (row.displayProfit === null) return false;
            if (row.displayProfitRate === null) return false;
            return true;
        });

        const calculableCount = calculableRows.length;
        const excludedCount = rows.length - calculableCount;

        if (calculableCount === 0) {
            return {
                totalSales,
                totalProfit: null as number | null,
                avgRate: null as number | null,
                deficitCount: null as number | null,
                productCount: rows.length,
                calculableCount,
                excludedCount,
            };
        }

        const calculableSales = calculableRows.reduce((acc, row) => acc + num(row.sales), 0);
        const totalProfit = calculableRows.reduce(
            (acc, row) => acc + (row.displayProfit ?? 0),
            0,
        );

        const avgRate =
            calculableSales > 0 ? totalProfit / calculableSales : null;

        const deficitCount = calculableRows.filter(
            (row) => (row.displayProfit ?? 0) < 0,
        ).length;

        return {
            totalSales,
            totalProfit,
            avgRate,
            deficitCount,
            productCount: rows.length,
            calculableCount,
            excludedCount,
        };
    }, [aggregatedRows]);

    const rangeLabel = useMemo(() => {
        const start = data?.range?.startDate ?? startDate;
        const end = data?.range?.endDate ?? endDate;
        if (!start || !end) return "";
        return `${start} 〜 ${end}`;
    }, [data?.range?.startDate, data?.range?.endDate, startDate, endDate]);

    const periodLabel = useMemo(() => {
        if (mode !== "month") return null;
        const d = new Date();
        const month = d.getMonth() + 1;
        const day = d.getDate();
        const totalDays = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        const pct = Math.round((day / totalDays) * 100);
        return `${month}月 累計（${day}日分・月の${pct}%経過）`;
    }, [mode]);

    const openDetail = useCallback(
        (row: DisplayRow) => {
            if (isRestrictedPlan) return;

            const itemKey = String(row.itemKey || "").trim();
            const itemManagementNumber = String(row.itemManagementNumber || row.itemCode || "").trim();
            const skuManagementNumber = String(row.skuManagementNumber || row.skuCode || "").trim();
            if (aggregateUnit === "sku" && !itemKey && !skuManagementNumber) return;
            if (aggregateUnit === "item" && !itemManagementNumber) return;

            const basePrefix = location.pathname.startsWith("/demo") ? "/demo" : "/app";
            const p = new URLSearchParams();
            if (itemKey) {
                p.set("itemKey", itemKey);
            }
            p.set("aggregateUnit", aggregateUnit);
            if (itemManagementNumber) {
                p.set("itemManagementNumber", itemManagementNumber);
            }
            if (skuManagementNumber) {
                p.set("skuManagementNumber", skuManagementNumber);
            }
            p.set("mode", mode);
            p.set("profitMode", profitMode);
            // 一覧表示と同じ実データ期間で詳細を開く（選択値と実績レンジのズレを吸収）
            const detailStartDate = String(data?.range?.startDate || startDate || "").trim();
            const detailEndDate = String(data?.range?.endDate || endDate || "").trim();
            p.set("startDate", detailStartDate);
            p.set("endDate", detailEndDate);

            navigate(`${basePrefix}/itemProfitDetail?${p.toString()}`);
        },
        [
            location.pathname,
            mode,
            profitMode,
            startDate,
            endDate,
            data?.range?.startDate,
            data?.range?.endDate,
            navigate,
            isRestrictedPlan,
            aggregateUnit,
        ],
    );

    const exportCurrentCsv = useCallback(() => {
        if (isRestrictedPlan) return;

        const csv = toCsv(filteredRows, profitMode);
        const ymd = (data?.range?.endDate ?? endDate).replace(/-/g, "");
        downloadCsv(`item_profit_list_${profitMode}_${ymd || "export"}.csv`, csv);
    }, [filteredRows, profitMode, data?.range?.endDate, endDate, isRestrictedPlan]);

    const fetchItemImageUrl = useCallback(
        async (row: DisplayRow, e: React.MouseEvent<HTMLButtonElement>) => {
            if (isRestrictedPlan) return;

            e.stopPropagation();

            const itemKey = String(row.itemKey || "").trim();
            const itemCode = String(row.itemCode || row.itemManagementNumber || "").trim();
            if (!itemKey || !itemCode) return;

            const yearMonthBase = (data?.range?.endDate ?? endDate).slice(0, 7);
            const yearMonth = /^\d{4}-\d{2}$/.test(yearMonthBase)
                ? `${yearMonthBase}-01`
                : `${today.slice(0, 7)}-01`;

            setImageFetchingItemKeys((prev) => new Set([...prev, itemKey]));
            try {
                const res = (await apiPutJson(
                    "/api/profit/item-image-url/fetch",
                    { yearMonth, itemKey, itemCode },
                )) as { imageUrl?: string };

                const nextImageUrl = String(res?.imageUrl || "").trim();
                if (nextImageUrl) {
                    setData((prev) => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            rows: prev.rows.map((r) =>
                                String(r.itemKey || "") === itemKey
                                    ? { ...r, imageUrl: nextImageUrl }
                                    : r,
                            ),
                        };
                    });
                } else {
                    await fetchData();
                }
            } catch (err: any) {
                const msg = err?.message || "画像URLの取得に失敗しました。";
                setError(msg);
            } finally {
                setImageFetchingItemKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(itemKey);
                    return next;
                });
            }
        },
        [data?.range?.endDate, endDate, fetchData, today, isRestrictedPlan],
    );

    // --- 表示中ページの画像を自動取得 ---
    const autoImageFetchedKeys = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (isRestrictedPlan) return;
        if (!data || loading) return;

        const yearMonthBase = (data.range?.endDate ?? endDate).slice(0, 7);
        const yearMonth = /^\d{4}-\d{2}$/.test(yearMonthBase)
            ? `${yearMonthBase}-01`
            : `${today.slice(0, 7)}-01`;

        const targets = pageRows.filter((r) => {
            const key = String(r.itemKey || "").trim();
            const code = String(r.itemCode || r.itemManagementNumber || "").trim();
            const url = String(r.imageUrl || "").trim();
            return key && code && !url && !autoImageFetchedKeys.current.has(key);
        });
        if (targets.length === 0) return;

        // 全対象にマーク＆並列フェッチ（最大5件同時）
        for (const row of targets) {
            autoImageFetchedKeys.current.add(String(row.itemKey));
        }

        (async () => {
            // 5件ずつ並列で処理
            for (let i = 0; i < targets.length; i += 5) {
                const batch = targets.slice(i, i + 5);
                await Promise.all(
                    batch.map(async (row) => {
                        const itemKey = String(row.itemKey || "").trim();
                        const itemCode = String(row.itemCode || row.itemManagementNumber || "").trim();
                        try {
                            const res = (await apiPutJson(
                                "/api/profit/item-image-url/fetch",
                                { yearMonth, itemKey, itemCode },
                            )) as { imageUrl?: string };

                            const nextImageUrl = String(res?.imageUrl || "").trim();
                            if (nextImageUrl) {
                                setData((prev) => {
                                    if (!prev) return prev;
                                    return {
                                        ...prev,
                                        rows: prev.rows.map((r) =>
                                            String(r.itemKey || "") === itemKey
                                                ? { ...r, imageUrl: nextImageUrl }
                                                : r,
                                        ),
                                    };
                                });
                            }
                        } catch {
                            // 自動取得失敗は無視
                        }
                    }),
                );
            }
        })();
    }, [pageRows, data, loading, endDate, today, isRestrictedPlan]);

    return (
        <div className="ipp-page">
            {loading && (
                <div className="ipp-loading">
                    <LoadingInline />
                </div>
            )}

            <ItemProfitUpgradeDialog
                open={showUpgradeDialog}
                onClose={() => setShowUpgradeDialog(false)}
            />

            <div className={isRestrictedPlan ? "pointer-events-none select-none opacity-50" : ""}>

            {alerts
                .filter((a) => !dismissedAlerts.has(a.key))
                .map((a) => (
                    <div key={a.key} className={`ipp-alert-banner ${a.type}`}>
                        <span className="ipp-alert-icon">{a.type === "red" ? "⚠️" : "💡"}</span>
                        <span className="ipp-alert-msg">{a.message}</span>
                        {a.ctaLabel && a.ctaPageId && (
                            <button
                                type="button"
                                className="ipp-alert-cta"
                                onClick={() => {
                                    const base = location.pathname.startsWith("/demo") ? "/demo" : "/app";
                                    navigate(`${base}/${a.ctaPageId}`);
                                }}
                            >
                                {a.ctaLabel}
                            </button>
                        )}
                        <button
                            className="ipp-alert-close"
                            type="button"
                            onClick={() =>
                                setDismissedAlerts((prev) => new Set([...prev, a.key]))
                            }
                        >
                            ✕
                        </button>
                    </div>
                ))}

            <div className="ipp-topbar">
                <div className="ipp-page-title">商品別利益</div>

                <div className="ipp-topbar-right">
                    <button
                        className={`ipp-period-btn ${selectedPeriodPreset === "current" ? "active" : ""}`}
                        onClick={() => selectPeriodPreset("current")}
                        disabled={loading}
                        type="button"
                    >
                        当月
                    </button>

                    <button
                        className={`ipp-period-btn ${selectedPeriodPreset === "previous" ? "active" : ""}`}
                        onClick={() => selectPeriodPreset("previous")}
                        disabled={loading}
                        type="button"
                    >
                        前月
                    </button>

                    <button
                        className={`ipp-period-btn ${selectedPeriodPreset === "twoMonthsAgo" ? "active" : ""}`}
                        onClick={() => selectPeriodPreset("twoMonthsAgo")}
                        disabled={loading}
                        type="button"
                    >
                        前々月
                    </button>

                    <button
                        className={`ipp-period-btn ${selectedPeriodPreset === "custom" ? "active" : ""}`}
                        onClick={() => setMode("custom")}
                        disabled={loading}
                        type="button"
                    >
                        期間指定
                    </button>

                    <input
                        type="date"
                        className="ipp-date-input"
                        value={startDate}
                        min={requestMinDate}
                        max={endDate}
                        onInput={(e) => {
                            const input = e.target as HTMLInputElement;
                            const next = clampDate(input.value, requestMinDate, endDate);
                            if (next !== startDate) {
                                setStartDate(next);
                            }
                        }}
                        onChange={(e) => {
                            setMode("custom");
                            const next = clampDate(e.target.value, requestMinDate, requestMaxDate);
                            setStartDate(next);
                            if (next > endDate) {
                                setEndDate(next);
                            }
                        }}
                        onBlur={(e) => {
                            const next = clampDate(e.target.value, requestMinDate, endDate);
                            if (next !== startDate) {
                                setStartDate(next);
                            }
                        }}
                    />
                    <span className="ipp-date-sep">〜</span>
                    <input
                        type="date"
                        className="ipp-date-input"
                        value={endDate}
                        min={startDate}
                        max={requestMaxDate}
                        onInput={(e) => {
                            const input = e.target as HTMLInputElement;
                            const next = clampDate(input.value, startDate, requestMaxDate);
                            if (next !== endDate) {
                                setEndDate(next);
                            }
                        }}
                        onChange={(e) => {
                            setMode("custom");
                            const next = clampDate(e.target.value, requestMinDate, requestMaxDate);
                            setEndDate(next);
                            if (next < startDate) {
                                setStartDate(next);
                            }
                        }}
                        onBlur={(e) => {
                            const next = clampDate(e.target.value, startDate, requestMaxDate);
                            if (next !== endDate) {
                                setEndDate(next);
                            }
                        }}
                    />
                    <span className="ipp-available-range-label">
                        選択可能期間: {minSelectableDate} 〜 {maxSelectableDate}
                    </span>
                    {periodLabel && (
                        <span className="ipp-period-label">{periodLabel}</span>
                    )}
                </div>
            </div>

            <div className="ipp-mode-bar">
                <span className="ipp-mode-label">表示モード：</span>

                <div className="ipp-mode-toggle">
                    <button
                        className={`ipp-mode-btn ${profitMode === "margin" ? "active" : ""}`}
                        type="button"
                        onClick={() => setProfitMode("margin")}
                    >
                        限界利益モード
                    </button>
                    <button
                        className={`ipp-mode-btn ${profitMode === "operating" ? "active" : ""}`}
                        type="button"
                        onClick={() => setProfitMode("operating")}
                    >
                        営業利益モード
                    </button>
                </div>

                <div className="ipp-mode-desc">
                    {profitMode === "margin" ? (
                        <>
                            <strong>限界利益</strong>：売上から変動費を差し引いた利益。固定費は含みません。
                        </>
                    ) : (
                        <>
                            <strong>営業利益</strong>：限界利益から固定費按分を差し引いた利益です。
                        </>
                    )}
                </div>

                <span className="ipp-mode-label">集計単位：</span>

                <div className="ipp-mode-toggle">
                    <button
                        className={`ipp-mode-btn ${aggregateUnit === "sku" ? "active" : ""}`}
                        type="button"
                        onClick={() => setAggregateUnit("sku")}
                    >
                        SKU
                    </button>
                    <button
                        className={`ipp-mode-btn ${aggregateUnit === "item" ? "active" : ""}`}
                        type="button"
                        onClick={() => setAggregateUnit("item")}
                    >
                        商品
                    </button>
                </div>

                <div className="ipp-mode-desc">
                    {aggregateUnit === "sku" ? (
                        <>
                            <strong>SKU単位</strong>：SKUごとの詳細で利益を比較します。
                        </>
                    ) : (
                        <>
                            <strong>商品単位</strong>：商品管理番号ごとにSKUをまとめて集計します。
                        </>
                    )}
                </div>
            </div>

            {error && <div className="ipp-error">{error}</div>}

            <div className="ipp-kpi-row">
                <div className="ipp-kpi-card">
                    <div className="ipp-kpi-label">売上合計</div>
                    <div className="ipp-kpi-value">{fmtYen(kpiSummary.totalSales)}</div>
                    <div className="ipp-kpi-sub">{rangeLabel}</div>
                </div>

                <div className="ipp-kpi-card">
                    <div className="ipp-kpi-label">
                        {profitMode === "margin" ? "限界利益合計" : "営業利益合計"}
                    </div>
                    <div
                        className={`ipp-kpi-value ${
                            kpiSummary.totalProfit === null
                                ? ""
                                : kpiSummary.totalProfit < 0
                                ? "neg"
                                : "pos"
                        }`}
                    >
                        {fmtYen(kpiSummary.totalProfit)}
                    </div>
                    <div className="ipp-kpi-sub">
                        {kpiSummary.calculableCount === 0
                            ? "原価未設定のため未算出"
                            : kpiSummary.excludedCount > 0
                            ? `原価未設定 ${nf0.format(kpiSummary.excludedCount)}商品を除外`
                            : "全商品合計"}
                    </div>
                </div>

                <div className="ipp-kpi-card">
                    <div className="ipp-kpi-label">
                        {profitMode === "margin" ? "限界利益率（平均）" : "営業利益率（平均）"}
                    </div>
                    <div
                        className={`ipp-kpi-value ${
                            kpiSummary.avgRate === null
                                ? ""
                                : kpiSummary.avgRate < 0
                                ? "neg"
                                : "pos"
                        }`}
                    >
                        {fmtPct(kpiSummary.avgRate)}
                    </div>
                    <div className="ipp-kpi-sub">
                        {kpiSummary.calculableCount === 0
                            ? "原価未設定のため未算出"
                            : `算出対象 ${nf0.format(kpiSummary.calculableCount)}商品`}
                    </div>
                </div>

                <div className="ipp-kpi-card">
                    <div className="ipp-kpi-label">
                        {profitMode === "margin" ? "赤字商品数（限界利益）" : "赤字商品数（営業利益）"}
                    </div>
                    <div className={`ipp-kpi-value ${kpiSummary.deficitCount === null ? "" : "neg"}`}>
                        {kpiSummary.deficitCount === null
                            ? "—"
                            : `${nf0.format(kpiSummary.deficitCount)}商品`}
                    </div>
                    <div className="ipp-kpi-sub">
                        {kpiSummary.calculableCount === 0
                            ? "原価未設定のため未算出"
                            : kpiSummary.excludedCount > 0
                            ? `未設定 ${nf0.format(kpiSummary.excludedCount)}商品あり`
                            : "要確認"}
                    </div>
                </div>
            </div>

            <div className="ipp-template-row">
                <span className="ipp-template-section-label">表示テンプレート：</span>

                <div className="ipp-template-cards">
                    <div
                        className={`ipp-template-card ${template === "A" ? "active" : ""}`}
                        onClick={() => setTemplate("A")}
                    >
                        <div className="ipp-template-card-icon">📊</div>
                        <div className="ipp-template-card-text">
                            <div className="ipp-template-card-name">利益確認用</div>
                            <div className="ipp-template-card-desc">売上・利益額・利益率・個数</div>
                        </div>
                        <div className="ipp-template-card-dot" />
                    </div>

                    <div
                        className={`ipp-template-card ${template === "B" ? "active" : ""}`}
                        onClick={() => setTemplate("B")}
                    >
                        <div className="ipp-template-card-icon">📣</div>
                        <div className="ipp-template-card-text">
                            <div className="ipp-template-card-name">広告効果確認用</div>
                            <div className="ipp-template-card-desc">売上・広告費・ROAS・利益額</div>
                        </div>
                        <div className="ipp-template-card-dot" />
                    </div>

                    <div
                        className={`ipp-template-card ${template === "C" ? "active" : ""}`}
                        onClick={() => setTemplate("C")}
                    >
                        <div className="ipp-template-card-icon">🔍</div>
                        <div className="ipp-template-card-text">
                            <div className="ipp-template-card-name">コスト分解用</div>
                            <div className="ipp-template-card-desc">原価・費用率・利益額</div>
                        </div>
                        <div className="ipp-template-card-dot" />
                    </div>
                </div>
            </div>

            <div className="ipp-filter-bar">
                <span className="ipp-filter-search-icon">🔍</span>
                <input
                    className="ipp-search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="商品名 / 商品番号で検索"
                />
                <span className="ipp-filter-sep" />
                <select
                    className="ipp-genre-select"
                    value={genreFilter}
                    onChange={(e) => setGenreFilter(e.target.value)}
                >
                    <option value="">ジャンル全て</option>
                    {genreOptions.map((g) => (
                        <option key={g} value={g}>{g}</option>
                    ))}
                </select>
                {showShippingType && (
                    <select
                        className="ipp-genre-select"
                        value={shippingFilter}
                        onChange={(e) => setShippingFilter(e.target.value === "separate" ? "separate" : "")}
                    >
                        <option value="">送料：すべて</option>
                        <option value="separate">送料別だけ</option>
                    </select>
                )}
                <span className="ipp-filter-count">
                    {search || genreFilter || (showShippingType && shippingFilter)
                        ? `全 ${aggregatedRows.length}件中 ${filteredRows.length}件絞り込み中`
                        : `全 ${filteredRows.length}件`}
                </span>
            </div>

            <div className="ipp-toolbar">
                <div className="ipp-tab-group">
                    <button
                        className={`ipp-tab ${tab === "all" ? "active" : ""}`}
                        onClick={() => setTab("all")}
                        type="button"
                    >
                        全商品
                    </button>
                    <button
                        className={`ipp-tab ${tab === "top" ? "active" : ""}`}
                        onClick={() => setTab("top")}
                        type="button"
                    >
                        TOP 10
                    </button>
                    <button
                        className={`ipp-tab ${tab === "worst" ? "active" : ""}`}
                        onClick={() => setTab("worst")}
                        type="button"
                    >
                        WORST 10
                    </button>
                </div>

                <div className="ipp-right-tools">
                    <span className="ipp-sort-label">並び替え：</span>

                    <button
                        className={`ipp-sort-btn ${sortKey === "sales" ? "active" : ""}`}
                        onClick={() => selectButtonSort("sales")}
                        type="button"
                    >
                        売上順
                    </button>
                    <button
                        className={`ipp-sort-btn ${sortKey === "qty" ? "active" : ""}`}
                        onClick={() => selectButtonSort("qty")}
                        type="button"
                    >
                        個数順
                    </button>
                    <button
                        className={`ipp-sort-btn ${sortKey === "profit" ? "active" : ""}`}
                        onClick={() => selectButtonSort("profit")}
                        type="button"
                    >
                        利益額順
                    </button>
                    <button
                        className={`ipp-sort-btn ${sortKey === "deficit" ? "active" : ""}`}
                        onClick={() => selectButtonSort("deficit")}
                        type="button"
                    >
                        赤字額順
                    </button>

                    <span className="ipp-divider-v" />

                    <button className="ipp-csv-btn" type="button" onClick={exportCurrentCsv}>
                        ⬇ CSV出力
                    </button>
                </div>
            </div>

            <div className="ipp-legend-bar">
                <span className="ipp-legend-prefix">凡例：</span>
                <span className="ipp-legend-item">
                    <span className="ipp-legend-line top" />
                    <span className="ipp-badge top">TOP</span>
                    利益額TOP10
                </span>
                <span className="ipp-legend-sep">|</span>
                <span className="ipp-legend-item">
                    <span className="ipp-legend-line worst" />
                    <span className="ipp-badge worst">WORST</span>
                    利益額下位TOP10
                </span>
                <span className="ipp-legend-sep">|</span>
                <span className="ipp-legend-item">
                    <span className="ipp-badge red">赤字</span>
                    利益がマイナス
                </span>
                <span className="ipp-legend-sep">|</span>
                <span className="ipp-legend-item">
                    <span className="ipp-badge warn">未設定</span>
                    原価未設定
                </span>
                {showShippingType && (
                    <>
                        <span className="ipp-legend-sep">|</span>
                        <span className="ipp-legend-item">
                            <span className="ipp-badge ship">送料別</span>
                            お客様が払った送料を売上に含む
                        </span>
                    </>
                )}
                <span className="ipp-legend-sep">|</span>
                {/* RN-238：楽天市場広告は商品ごとのデータが無いため商品別には入れていない。商品の合計がお店全体の利益と合わない理由をここで示す（同じ注記に「 / 」でつなぐ＝この帯の決まり） */}
                <span className="ipp-legend-note">※売上はクーポン値引き前の金額です / 利益額が「未算出」の行は原価未設定などで計算できていません{mall === "rakuten" ? " / 楽天広告（楽天市場・楽天グループの広告）は商品ごとに分けられないため、商品別の利益には入れていません（お店全体の利益には入っています）" : ""}</span>
            </div>

            <div className="ipp-table-box">
                <table className="ipp-table">
                    {template === "A" && (
                        <colgroup>
                            <col style={{ width: "128px" }} />
                            <col style={{ width: "52px" }} />
                            <col />
                            <col style={{ width: "100px" }} />
                            <col style={{ width: "110px" }} />
                            <col style={{ width: "78px" }} />
                            <col style={{ width: "68px" }} />
                            <col style={{ width: "86px" }} />
                            <col style={{ width: "72px" }} />
                        </colgroup>
                    )}
                    {template === "B" && (
                        <colgroup>
                            <col style={{ width: "128px" }} />
                            <col style={{ width: "52px" }} />
                            <col />
                            <col style={{ width: "100px" }} />
                            <col style={{ width: "88px" }} />
                            <col style={{ width: "88px" }} />
                            <col style={{ width: "88px" }} />
                            <col style={{ width: "78px" }} />
                            <col style={{ width: "110px" }} />
                            <col style={{ width: "86px" }} />
                            <col style={{ width: "72px" }} />
                        </colgroup>
                    )}
                    {template === "C" && (
                        <colgroup>
                            <col style={{ width: "128px" }} />
                            <col style={{ width: "52px" }} />
                            <col />
                            <col style={{ width: "100px" }} />
                            <col style={{ width: "88px" }} />
                            <col style={{ width: "76px" }} />
                            <col style={{ width: "88px" }} />
                            <col style={{ width: "76px" }} />
                            <col style={{ width: "110px" }} />
                            <col style={{ width: "78px" }} />
                            <col style={{ width: "86px" }} />
                            <col style={{ width: "72px" }} />
                        </colgroup>
                    )}
                    <thead>
                        {template === "A" && (
                            <tr>
                                <th>{aggregateUnit === "item" ? "商品管理番号" : "商品管理番号 / SKU管理番号"}</th>
                                <th style={{ textAlign: "center", width: "52px" }}>画像</th>
                                <th>商品名</th>
                                {renderSortTh("売上", "sales")}
                                {renderSortTh(profitMode === "margin" ? "利益額" : "営業利益額", "profit")}
                                {renderSortTh(profitMode === "margin" ? "利益率" : "営業利益率", "profitRate")}
                                {renderSortTh("個数", "qty")}
                                <th className="center">状態</th>
                                <th className="center">画像取得</th>
                            </tr>
                        )}

                        {template === "B" && (
                            <tr>
                                <th>{aggregateUnit === "item" ? "商品管理番号" : "商品管理番号 / SKU管理番号"}</th>
                                <th style={{ textAlign: "center", width: "52px" }}>画像</th>
                                <th>商品名</th>
                                {renderSortTh("売上", "sales")}
                                {renderSortTh("広告費", "adCost")}
                                {renderSortTh("12時間ROAS", "roas12h")}
                                {renderSortTh("720時間ROAS", "roas720h")}
                                {renderSortTh("CA ROAS", "caRoas")}
                                {renderSortTh(profitMode === "margin" ? "利益額" : "営業利益額", "profit")}
                                <th className="center">状態</th>
                                <th className="center">画像取得</th>
                            </tr>
                        )}

                        {template === "C" && (
                            <tr>
                                <th>{aggregateUnit === "item" ? "商品管理番号" : "商品管理番号 / SKU管理番号"}</th>
                                <th style={{ textAlign: "center", width: "52px" }}>画像</th>
                                <th>商品名</th>
                                {renderSortTh("売上", "sales")}
                                {renderSortTh("原価", "cost")}
                                {renderSortTh("原価率", "costRate")}
                                {renderSortTh("費用合計", "feeUnit")}
                                {renderSortTh("費用率", "feeRate")}
                                {renderSortTh(profitMode === "margin" ? "利益額" : "営業利益額", "profit")}
                                {renderSortTh(profitMode === "margin" ? "利益率" : "営業利益率", "profitRate")}
                                <th className="center">状態</th>
                                <th className="center">画像取得</th>
                            </tr>
                        )}
                    </thead>

                    <tbody>
                        {!loading && pageRows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={template === "A" ? 9 : template === "B" ? 11 : 12}
                                    className="ipp-empty"
                                >
                                    データがありません
                                </td>
                            </tr>
                        )}

                        {pageRows.map((r, idx) => {
                            const displayId =
                                r.itemManagementNumber ||
                                r.itemNumber ||
                                r.itemCode ||
                                "—";
                            const displaySkuId =
                                r.skuManagementNumber ||
                                r.skuNumber ||
                                r.skuCode ||
                                r.itemKey ||
                                "—";

                            const profitClass =
                                r.displayProfit === null ? "" : r.displayProfit < 0 ? "neg" : "pos";
                            const profitRateClass =
                                r.displayProfitRate === null ? "" : r.displayProfitRate < 0 ? "neg" : "pos";

                            return (
                                <tr
                                    key={`${r.itemKey || displayId}-${idx}`}
                                    className={
                                        topWorstKeySet.top.has(String(r.itemKey || ""))
                                            ? "row-top"
                                            : topWorstKeySet.worst.has(String(r.itemKey || ""))
                                            ? "row-worst"
                                            : ""
                                    }
                                    onClick={() => openDetail(r)}
                                            style={{
                                                cursor:
                                                    (aggregateUnit === "sku" && !!r.itemKey) ||
                                                    (aggregateUnit === "item" && !!(r.itemManagementNumber || r.itemCode))
                                                        ? "pointer"
                                                        : "default",
                                            }}
                                >
                                    <td className="mono">
                                        {data?.shopUrl && r.itemManagementNumber ? (
                                            <a
                                                href={`https://item.rakuten.co.jp/${data.shopUrl}/${r.itemManagementNumber}/`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="ipp-item-link"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {displayId}
                                            </a>
                                        ) : (
                                            displayId
                                        )}
                                        {aggregateUnit === "sku" && (
                                            <div className="ipp-sub-id">SKU: {displaySkuId}</div>
                                        )}
                                    </td>

                                    <td className="ipp-thumb-col">
                                        <div className="ipp-thumb-wrap">
                                            <IppThumb imageUrl={r.imageUrl} genreName={r.genreName} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ipp-prod-text">
                                            <div className="ipp-prod-name">{r.itemName || "—"}</div>
                                            <div className="ipp-prod-genre">
                                                {showShippingType && num(r.hasSeparateShipping) > 0 && (
                                                    <span className="ipp-badge ship ipp-prod-badge">送料別</span>
                                                )}
                                                {r.genreName || ""}
                                            </div>
                                        </div>
                                    </td>

                                    {template === "A" && (
                                        <>
                                            <td className="right mono">{fmtYen(r.sales)}</td>
                                            <td className={`right mono ${profitClass}`}>
                                                <div>{fmtYen(r.displayProfit)}</div>
                                                {(() => {
                                                    const rate = profitMode === "margin"
                                                        ? nullableNum(r.momProfitChangeRate)
                                                        : nullableNum(r.momOpProfitChangeRate);
                                                    if (rate === null) return null;
                                                    const sign = rate >= 0 ? "+" : "";
                                                    const cls = rate >= 0 ? "mom-pos" : "mom-neg";
                                                    return (
                                                        <div className={`ipp-mom ${cls}`}>
                                                            {`前月同期比 ${sign}${(rate * 100).toFixed(1)}%`}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className={`right mono ${profitRateClass}`}>
                                                {fmtPct(r.displayProfitRate)}
                                            </td>
                                            <td className="right mono">{nf0.format(num(r.units))}</td>
                                        </>
                                    )}

                                    {template === "B" && (
                                        <>
                                            <td className="right mono">{fmtYen(r.sales)}</td>
                                            <td className="right mono">{fmtYen(r.adCostTotal)}</td>
                                            <td className="right mono">{fmtRoasWhole(r.rppRoas12h)}</td>
                                            <td className="right mono">{fmtRoasWhole(r.rppRoas720h)}</td>
                                            <td className="right mono">{fmtRoasWhole(r.caRoas)}</td>
                                            <td className={`right mono ${profitClass}`}>
                                                {fmtYen(r.displayProfit)}
                                            </td>
                                        </>
                                    )}

                                    {template === "C" && (
                                        <>
                                            <td className="right mono">{fmtYen(r.sales)}</td>
                                            <td className="right mono">{fmtYen(r.cost)}</td>
                                            <td className="right mono">{fmtPct(r.costRate)}</td>
                                            <td className="right mono">{fmtYen(r.feeUnit)}</td>
                                            <td className="right mono">{fmtPct(r.feeRate)}</td>
                                            <td className={`right mono ${profitClass}`}>
                                                {fmtYen(r.displayProfit)}
                                            </td>
                                            <td className={`right mono ${profitRateClass}`}>
                                                {fmtPct(r.displayProfitRate)}
                                            </td>
                                        </>
                                    )}

                                    <td className="center">
                                        {num(r.costMissingFlag) > 0 ? (
                                            <span className="ipp-status warn">未設定</span>
                                        ) : r.displayProfit !== null && r.displayProfit < 0 ? (
                                            <span className="ipp-status bad">赤字</span>
                                        ) : r.status === "good" ? (
                                            <span className="ipp-status good">TOP候補</span>
                                        ) : r.status === "warn" ? (
                                            <span className="ipp-status warn">要確認</span>
                                        ) : (
                                            <span className="ipp-status warn">—</span>
                                        )}
                                    </td>

                                    <td className="center">
                                        <button
                                            type="button"
                                            className="ipp-image-fetch-mini-btn"
                                            onClick={(e) => fetchItemImageUrl(r, e)}
                                            disabled={imageFetchingItemKeys.has(String(r.itemKey || ""))}
                                        >
                                            {imageFetchingItemKeys.has(String(r.itemKey || ""))
                                                ? "取得中"
                                                : String(r.imageUrl || "").trim()
                                                    ? "再取得"
                                                    : "取得"}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div className="ipp-pagination">
                    <div className="ipp-pagination-left">
                        <span className="ipp-pagination-count-label">表示件数：</span>
                        <select
                            className="ipp-count-select"
                            value={pageSize}
                            onChange={(e) => {
                                setPage(1);
                                setPageSize(num(e.target.value));
                            }}
                        >
                            <option value={15}>15件</option>
                            <option value={30}>30件</option>
                            <option value={50}>50件</option>
                            <option value={100}>100件</option>
                        </select>
                        <span className="ipp-pagination-info">
                            {filteredRows.length === 0
                                ? "0件"
                                : `${(currentPage - 1) * pageSize + 1}〜${Math.min(
                                      currentPage * pageSize,
                                      filteredRows.length,
                                  )} / 全${filteredRows.length}件`}
                        </span>
                    </div>

                    <div className="ipp-pagination-right">
                        <button
                            className="ipp-page-btn"
                            type="button"
                            disabled={currentPage <= 1}
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        >
                            ← 前へ
                        </button>

                        <span className="ipp-page-now">
                            {currentPage} / {totalPages}
                        </span>

                        <button
                            className="ipp-page-btn"
                            type="button"
                            disabled={currentPage >= totalPages}
                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                        >
                            次へ →
                        </button>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}